/**
 * Canonical timezone helper — the single implementation shared by client and
 * server. #2322 (Phase 1 of the #2321 timezone-standardization epic).
 *
 * ── Storage contract ─────────────────────────────────────────────────────
 *
 * `Location.locationTimezone` stores the **Tracx V1 abbreviation key** —
 * `AT / ET / CT / MT / PT / MST / HST` (V1 `config/global.php` → `time_zone`).
 * Zones those seven keys cannot express store their full IANA id instead.
 *
 * That mixed shape is deliberate and V1-compatible: V1's
 * `TimeZoneTrait::timezoneNameFromAbbr` returns an unrecognized value
 * UNCHANGED and hands it straight to `new DateTimeZone(...)`, so an IANA name
 * in that column resolves correctly in V1 today. V1 production data already
 * contains such values.
 *
 * Why not store IANA everywhere: the seven keys are the format V1 reads and
 * writes, and matching it keeps import/export/EDI interop free. Why not store
 * ONLY keys: they cannot express `America/St_Johns` (UTC−3:30) or anything
 * outside North America, which V1's passthrough already accommodates.
 *
 * ── DST ──────────────────────────────────────────────────────────────────
 *
 * The abbreviation is a LOOKUP KEY, never an offset. Every key resolves to a
 * real IANA zone, so DST is applied by `Intl` from there on. The one place
 * DST can go wrong is the REVERSE direction, and V1 gets it wrong: both copies
 * of V1's `getAbbrFromTimezoneName()` classify a zone by
 * `(new DateTime('now', $tz))->format('T')` — the abbreviation *at the moment
 * the job runs*. `America/Denver` classified in January formats as `MST`, is
 * stored as `MST`, and resolves back to `America/Phoenix`, losing DST
 * permanently; classified in July it formats `MDT` → `MT` → `America/Denver`.
 * The stored value depended on the calendar month the backfill executed.
 *
 * {@link ianaToV1Key} keeps V1's family grouping but never consults "now" —
 * it reads the abbreviation at the zone's daylight extreme — so `America/Denver`
 * always lands on `MT` and `America/Phoenix` always on `MST`.
 *
 * That family grouping is coarser than true offset equivalence, and adopting
 * it is a deliberate call (Andrew, 2026-08-07). See {@link ianaToV1Key} for
 * the three zones where the two disagree, and {@link zoneOffsetFingerprint}
 * for the exact-equivalence test callers can use instead.
 */
/**
 * V1 key → canonical IANA zone. Taken from V1's **JavaScript** mapping,
 * `resources/js/main.js::getTimeZoneName` (line 7882).
 *
 * ── Why the JS table and not the PHP trait ──────────────────────────────
 *
 * V1 has TWO mappings and they disagree on exactly one key:
 *
 *   | key | PHP `TimeZoneTrait::timezoneNameFromAbbr` | JS `getTimeZoneName` |
 *   | AT  | America/Halifax  (AST/ADT, observes DST)  | America/Anguilla (AST year-round, NO DST) |
 *
 * (PHP reaches Halifax via `timezone_name_from_abbr('ADT')`, verified against
 * php 8.3. The other six keys are identical in both.)
 *
 * The JS table wins because it is the one that drives the CALCULATIONS this
 * epic is porting — `trips/main.blade.php` feeds it to `moment.tz(...)` for
 * trip start/end and total time, and `bids/main.blade.php` (V2 Driver Routes)
 * does the same. The PHP trait only backs display-side formatting.
 *
 * Consequence, flagged deliberately: `AT` is a FIXED UTC−4 with no daylight
 * shift, yet V1's family rule files Atlantic *Canada* (Halifax, Moncton) under
 * it — so those locations lose their summer hour. That is V1's behaviour and
 * we match it; see {@link ianaToV1Key}.
 *
 * `MST` and `MT` are deliberately distinct: Arizona does not observe DST, and
 * that distinction is the whole reason V1 carries both keys.
 */
export const V1_TZ_KEY_TO_IANA = Object.freeze({
    AT: 'America/Anguilla',
    ET: 'America/New_York',
    CT: 'America/Chicago',
    MT: 'America/Denver',
    PT: 'America/Los_Angeles',
    MST: 'America/Phoenix',
    HST: 'Pacific/Honolulu',
});
/** Stable dropdown order — V1 `config/global.php` → `time_zone` key order. */
export const V1_TZ_KEYS = Object.freeze([
    'AT', 'ET', 'CT', 'MT', 'PT', 'MST', 'HST',
]);
/**
 * UI labels, verbatim from V1 `config/global.php` → `time_zone` VALUES.
 *
 * `AT` displays as `ADT` in V1. That is not a typo on our side — it is what
 * V1's dropdown renders, and this table exists to match it exactly.
 */
export const V1_TZ_KEY_LABELS = Object.freeze({
    AT: 'ADT',
    ET: 'ET',
    CT: 'CT',
    MT: 'MT',
    PT: 'PT',
    MST: 'MST',
    HST: 'HST',
});
export function isV1TimezoneKey(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(V1_TZ_KEY_TO_IANA, value);
}
/**
 * ── Why there is no legacy-abbreviation table here ──────────────────────
 *
 * An earlier cut of this module carried a `LEGACY_ABBR_TO_IANA` shim mapping
 * `MDT → America/Denver`, `ADT → America/Halifax` and friends. It has been
 * removed: it was invented here, not ported, and it contradicts V1.
 *
 * V1's RESOLVE path maps only the seven keys and passes everything else
 * through untouched to `new DateTimeZone(...)`. PHP happens to accept bare
 * abbreviations there and treats them as FIXED offsets — `MDT` becomes a flat
 * UTC−6, never `America/Denver`'s −7/−6. So the shim was not V1 parity; it
 * silently gave those rows a DST rule V1 never applied.
 *
 * The values it targeted (`MDT`, `AKDT`, `NDT`, `CEST`, `AEST`, `WAT`, …)
 * exist only as artifacts of V1's buggy `getAbbrFromTimezoneName()` fallback
 * (`return $dateTime->format('T')`) — they are corrupt data, not a format.
 * Guessing a zone for them is lossy either way, so {@link resolveTimeZone}
 * refuses them and the caller falls through to the Location or the
 * Organization. Phase 2 (#2323) re-resolves those rows from the Location's
 * lat/lng, which is correct rather than guessed. A few hundred rows total.
 */
// ─── Normalization + validation ────────────────────────────────────────────
/**
 * Strip a picker's display prefix from a stored value.
 *
 *     "(UTC-04:00) America/New_York"  ->  "America/New_York"
 *     "(GMT-05:00) America/New_York"  ->  "America/New_York"
 *
 * Both forms occur: the V2 pickers write `(UTC±HH:MM) `, V1's company
 * timezone uses `(GMT±HH:MM) ` (V1 recovers it with `explode(') ')`). Some
 * `organizations.timezone` rows persisted the LABEL rather than the value, and
 * `Intl` rejects those verbatim. Surrounding quotes are stripped too — they
 * turn up in values that round-tripped through JSON.
 *
 * Anything without such a prefix is returned trimmed but untouched.
 */
export function normalizeToIanaTimeZone(tz) {
    return tz.replace(/"/g, '').trim().replace(/^\([^)]*\)\s*/, '').trim();
}
/** `Intl` zone-id validity, memoized — this runs per grid row. */
const zoneValidity = new Map();
/** True when `Intl` accepts `tz` as a timezone id. */
export function isValidIanaTimeZone(tz) {
    const cached = zoneValidity.get(tz);
    if (cached !== undefined)
        return cached;
    let ok = false;
    try {
        new Intl.DateTimeFormat('en-CA', { timeZone: tz });
        ok = true;
    }
    catch {
        ok = false;
    }
    zoneValidity.set(tz, ok);
    return ok;
}
/**
 * Resolve any stored timezone value to a usable IANA id.
 *
 * Accepts, in priority order — the same order V1's resolve path uses:
 *   1. a V1 key (`ET`) — wins over `Intl` so `MST` resolves to the intended
 *      `America/Phoenix` rather than the fixed-offset legacy `MST` zone, and
 *      `HST` to `Pacific/Honolulu`;
 *   2. a full IANA id, with or without a `(UTC±HH:MM) ` / `(GMT±HH:MM) `
 *      label — including offset-form ids like `+06` and legacy fixed-offset
 *      ids like `EST` / `CET`, which `Intl` accepts and V1 passes through
 *      verbatim (V1's `EST → ET` mapping exists only in the CLASSIFY
 *      direction, never here, so `EST` correctly stays a flat UTC−5).
 *
 * Anything else is refused — see the note above on why there is no legacy
 * abbreviation table.
 *
 * Returns `null` when the value is blank or cannot be resolved — callers pick
 * their own fallback. Use {@link resolveTimeZoneOrUtc} for the V1 behaviour of
 * defaulting to UTC.
 *
 * This replaces the old `isValidIanaTimeZone`-gated skip, which silently
 * discarded `ET` / `CT` / `MT` / `PT` / `AT` — millions of rows.
 */
export function resolveTimeZone(stored) {
    if (typeof stored !== 'string')
        return null;
    const cleaned = normalizeToIanaTimeZone(stored);
    if (cleaned.length === 0)
        return null;
    const upper = cleaned.toUpperCase();
    if (isV1TimezoneKey(upper))
        return V1_TZ_KEY_TO_IANA[upper];
    if (isValidIanaTimeZone(cleaned))
        return cleaned;
    return null;
}
/**
 * {@link resolveTimeZone} with V1's `Helpers::getTimezone` default — an
 * empty or unresolvable value becomes `'UTC'`.
 */
export function resolveTimeZoneOrUtc(stored) {
    return resolveTimeZone(stored) ?? 'UTC';
}
// ─── Reverse mapping: IANA → V1 key ────────────────────────────────────────
/**
 * First year the offset fingerprints are sampled in.
 *
 * FIXED, not `new Date().getFullYear()`, so the classification is fully
 * deterministic and unit-testable — the whole point of this module (see the
 * header note on V1's month-dependent bug). Because both sides of every
 * comparison are sampled over the SAME window, a genuine DST rule change
 * affects them equally and the equivalence still holds.
 */
export const TZ_FINGERPRINT_YEAR = 2026;
/**
 * How many consecutive years the sample window covers.
 *
 * The window serves two callers: {@link zoneOffsetFingerprint} (exact
 * interchangeability) and {@link ianaToV1Key}, which scans it for a zone's
 * daylight extreme.
 *
 * A single year is not enough for either. Two zones can coincide for one year
 * and diverge the next — tzdata 2026b moves British Columbia to permanent DST
 * from Nov 2026, so `America/Vancouver` splits from `America/Los_Angeles`
 * mid-window, and during 2027 alone Vancouver is a flat −07:00 that is
 * offset-identical to `America/Phoenix`. A one-year sample would call
 * Vancouver interchangeable with one or the other depending on which year it
 * picked. Three years defeats both readings.
 *
 * (V1's family rule still files Vancouver under `PT` regardless, because its
 * abbreviation stays `PDT` — see {@link ianaToV1Key}. The window is what makes
 * the *fingerprint* honest about the difference for callers that need it.)
 */
export const TZ_FINGERPRINT_YEARS = 3;
/**
 * Sample instants — the 1st and 15th of every month across the window, at
 * 12:00 UTC. Any real DST period lasts weeks, so this grid cannot miss one,
 * and 12:00 UTC keeps every sample clear of transition instants (which occur
 * near local midnight or 2–3am).
 */
const SAMPLE_INSTANTS = Object.freeze(Array.from({ length: TZ_FINGERPRINT_YEARS }, (_, yearOffset) => Array.from({ length: 12 }, (_, month) => [
    Date.UTC(TZ_FINGERPRINT_YEAR + yearOffset, month, 1, 12),
    Date.UTC(TZ_FINGERPRINT_YEAR + yearOffset, month, 15, 12),
]).flat()).flat());
/** Offset (`wall − utc`, minutes) for `tz` at a UTC instant. `NaN` if unusable. */
function tzOffsetMinutesAt(utcMs, tz) {
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }).formatToParts(new Date(utcMs));
    }
    catch {
        return NaN;
    }
    const o = {};
    for (const p of parts)
        o[p.type] = p.value;
    const wallMs = Date.UTC(Number(o.year), Number(o.month) - 1, Number(o.day), Number(o.hour) % 24, Number(o.minute), Number(o.second ?? '0'));
    if (!Number.isFinite(wallMs))
        return NaN;
    return (wallMs - utcMs) / 60000;
}
const fingerprints = new Map();
/**
 * A zone's full-year offset signature — the joined offsets at every sample
 * instant. Two zones with identical signatures are interchangeable for every
 * date calculation in the app: same standard offset, same DST observance, same
 * transition dates.
 *
 * Returns `null` for a zone `Intl` cannot use.
 */
export function zoneOffsetFingerprint(tz) {
    const cached = fingerprints.get(tz);
    if (cached !== undefined)
        return cached;
    const offsets = [];
    for (const instant of SAMPLE_INSTANTS) {
        const off = tzOffsetMinutesAt(instant, tz);
        if (!Number.isFinite(off)) {
            fingerprints.set(tz, null);
            return null;
        }
        offsets.push(off);
    }
    const fp = offsets.join(',');
    fingerprints.set(tz, fp);
    return fp;
}
/**
 * V1's abbreviation → key families, from
 * `TimeZoneTrait::getAbbrFromTimezoneName()`'s switch. This IS V1's
 * classification rule; we reproduce it exactly, only deterministically
 * (see {@link ianaToV1Key}).
 */
const V1_ABBR_TO_KEY = Object.freeze({
    EST: 'ET', ET: 'ET', EDT: 'ET',
    CST: 'CT', CT: 'CT', CDT: 'CT',
    MST: 'MST', // V1 lists MST as its OWN key, above the MT family
    MT: 'MT', MDT: 'MT',
    PST: 'PT', PT: 'PT', PDT: 'PT',
    AST: 'AT', AT: 'AT', ADT: 'AT',
    HST: 'HST', HT: 'HST',
});
/** The `Intl` short zone name (`EST`, `MDT`, `GMT+5:30`, …) at an instant. */
function zoneAbbreviationAt(utcMs, tz) {
    try {
        const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
            .formatToParts(new Date(utcMs))
            .find((p) => p.type === 'timeZoneName');
        return part?.value ?? null;
    }
    catch {
        return null;
    }
}
/**
 * Reverse of {@link resolveTimeZone} — the value to STORE for a given zone.
 *
 * Reproduces V1's `getAbbrFromTimezoneName()` classification: read the zone's
 * abbreviation and map it through V1's families ({@link V1_ABBR_TO_KEY}).
 * Anything with no family match keeps its IANA name — V1's trait does the same
 * (`default: return $timezoneName`), and V1 resolves such values natively.
 *
 * ── The one change from V1 ──────────────────────────────────────────────
 *
 * V1 reads the abbreviation via `(new DateTime('now', $tz))->format('T')` — at
 * the moment the job runs — so `America/Denver` classified in January reads
 * `MST` and lands on the `MST` key (Phoenix, no DST), while in July it reads
 * `MDT` and lands on `MT`. The stored value depended on the calendar month.
 *
 * We take the abbreviation at the zone's DAYLIGHT extreme when it observes
 * DST, and at its standard offset when it does not. Denver therefore always
 * reads `MDT` → `MT`, and Phoenix always reads `MST` → `MST`, whatever the
 * date. Never consults the current date.
 *
 * ── Divergences from offset-equivalence, inherited from V1 ──────────────
 *
 * This is V1's rule, so it inherits V1's imprecision. Deliberate, per Andrew
 * 2026-08-07 ("let's consider v1 behavior"), and pinned by tests:
 *
 *   - `America/Halifax` / `America/Moncton` → `AT` → `America/Anguilla`,
 *     which has no DST. Atlantic Canada loses its summer hour.
 *   - `America/Regina` (CST year-round) → `CT` → `America/Chicago`, gaining
 *     DST it does not observe.
 *   - `America/Vancouver` → `PT`, losing the tzdata-2026b BC permanent-DST
 *     split from Nov 2026.
 *
 * {@link zoneOffsetFingerprint} remains available for callers that need true
 * interchangeability rather than V1's family grouping.
 */
export function ianaToV1Key(tz) {
    const resolved = resolveTimeZone(tz);
    if (resolved === null)
        return null;
    // Pick the sample instant at the zone's greatest offset — its daylight
    // extreme when it observes DST, and any instant when it does not (they are
    // all equal then). This is what makes the classification date-independent.
    let peakInstant = SAMPLE_INSTANTS[0];
    let peakOffset = -Infinity;
    for (const instant of SAMPLE_INSTANTS) {
        const off = tzOffsetMinutesAt(instant, resolved);
        if (!Number.isFinite(off))
            return resolved;
        if (off > peakOffset) {
            peakOffset = off;
            peakInstant = instant;
        }
    }
    const abbr = zoneAbbreviationAt(peakInstant, resolved);
    if (abbr === null)
        return resolved;
    return V1_ABBR_TO_KEY[abbr.toUpperCase()] ?? resolved;
}
/**
 * Display label for a stored value — the V1 label for a key, otherwise the
 * IANA id itself. For grids and read-only surfaces.
 */
export function timeZoneDisplayLabel(stored) {
    if (typeof stored !== 'string')
        return '';
    const cleaned = normalizeToIanaTimeZone(stored);
    if (cleaned.length === 0)
        return '';
    const upper = cleaned.toUpperCase();
    return isV1TimezoneKey(upper) ? V1_TZ_KEY_LABELS[upper] : cleaned;
}
// ─── Wall-clock helpers ────────────────────────────────────────────────────
/**
 * Render `instant` as the wall clock in `timeZone`, packed as fake-UTC — the
 * same shape the stop columns are stored in, so `column < wallClockInZone(now,
 * tz)` means "that wall-clock moment has passed at that stop".
 *
 * Returns `null` — rather than silently falling back to UTC — when the zone is
 * absent or unusable, so callers can choose their own fallback (an org-level
 * zone is usually a better guess than raw UTC).
 */
export function wallClockInZone(instant, timeZone) {
    const tz = resolveTimeZone(timeZone);
    if (tz === null)
        return null;
    if (Number.isNaN(instant.getTime()))
        return null;
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        }).formatToParts(instant);
        const get = (type) => {
            const raw = parts.find((p) => p.type === type)?.value;
            return raw === undefined ? NaN : Number(raw);
        };
        const y = get('year');
        const mo = get('month');
        const d = get('day');
        // `hour12: false` still emits '24' for midnight in some ICU versions. This
        // normalization applies to the HOUR ONLY — folding it into a shared getter
        // silently zeroed any minute / second / day that happened to be 24.
        const rawHour = get('hour');
        const h = rawHour === 24 ? 0 : rawHour;
        const mi = get('minute');
        const s = get('second');
        if (![y, mo, d, h, mi, s].every((n) => Number.isFinite(n)))
            return null;
        return new Date(Date.UTC(y, mo - 1, d, h, mi, s));
    }
    catch {
        return null;
    }
}
/**
 * Inverse of {@link wallClockInZone}: a Date whose UTC fields hold a wall-clock
 * reading in `timeZone` → the TRUE UTC instant.
 *
 * ── Why two round-trips (ATL-000618) ────────────────────────────────────
 *
 * The single-step estimate `U₀ = 2W − render(W)` evaluates the zone's offset
 * at the fake-UTC instant `W`, which sits hours from the true instant. When
 * the two straddle a DST transition the estimate lands an hour off — on New
 * York's spring-forward day, wall 04:00 resolved to 09:00Z instead of the true
 * 08:00Z. The refinement re-renders the estimate and applies the residual,
 * which converges for every real zone (offsets are piecewise-constant with
 * transitions at least an hour apart). On a non-DST day the residual is 0 and
 * the result is identical to the single-step form.
 *
 * Edge semantics, deterministic and documented:
 *   - fall-back AMBIGUOUS wall times resolve to the FIRST occurrence (the
 *     daylight one);
 *   - spring-forward NONEXISTENT wall times resolve to
 *     (true transition instant − remaining gap) — a defined nearby instant.
 *
 * #2330 — hoisted here from
 * `integrations/eld/samsara/geofence-polling/gps-geofence-window.ts`, which
 * was the FIFTH independent copy of the wall-clock↔UTC conversion in this
 * codebase and had to hand-add the correction above. Returns `null` on an
 * unusable zone or instant rather than guessing.
 */
export function utcFromWallClockInZone(wall, timeZone) {
    const tz = resolveTimeZone(timeZone);
    if (tz === null)
        return null;
    const W = wall.getTime();
    if (!Number.isFinite(W))
        return null;
    const render = (ms) => {
        const d = wallClockInZone(new Date(ms), tz);
        return d === null ? null : d.getTime();
    };
    const r1 = render(W);
    if (r1 === null)
        return null;
    let utc = 2 * W - r1;
    const r2 = render(utc);
    if (r2 === null)
        return null;
    utc += W - r2;
    return new Date(utc);
}
/**
 * Postgres-literal form of a fake-UTC wall clock: `YYYY-MM-DD HH:MM:SS`, cast
 * with `::timestamp` at the call site so the comparison stays naive-vs-naive
 * and never picks up the DB session's `TimeZone`.
 */
export function toPgTimestampLiteral(d) {
    return d.toISOString().slice(0, 19).replace('T', ' ');
}
//# sourceMappingURL=timezone.js.map